import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { X, Camera, Hand, Plus, Check, RotateCcw, Loader2 } from "lucide-react";
import AjusteEsquinas from "@/components/AjusteEsquinas";
import { loadCv, detectar, evaluar, movimiento, escanear, NITIDEZ_MIN } from "@/lib/scanner";

const AUTO_MS = 30000;
const SW = 360;

const canvasDe = (src, w, h) => { const c = document.createElement("canvas"); c.width = w; c.height = h; c.getContext("2d").drawImage(src, 0, 0, w, h); return c; };

async function armarPdf(hojas, nombre) {
  const pdf = new jsPDF({ unit: "pt", format: "letter" });
  const PW = 612, PH = 792, M = 18;
  hojas.forEach((h, i) => {
    if (i) pdf.addPage();
    const r = Math.min((PW - 2 * M) / h.canvas.width, (PH - 2 * M) / h.canvas.height);
    const w = h.canvas.width * r, hh = h.canvas.height * r;
    pdf.addImage(h.canvas.toDataURL("image/jpeg", 0.85), "JPEG", (PW - w) / 2, M, w, hh);
  });
  return new File([pdf.output("blob")], nombre, { type: "application/pdf" });
}

export default function EscanerRemision({ nombre, onDone, onCancel }) {
  const [fase, setFase] = useState("cargando");
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("Apunta a la remisión");
  const [verde, setVerde] = useState(false);
  const [restante, setRestante] = useState(30);
  const [aviso, setAviso] = useState("");
  const [captura, setCaptura] = useState(null);
  const [actual, setActual] = useState(null);
  const [hojas, setHojas] = useState([]);
  const [armando, setArmando] = useState(false);
  const [aspecto, setAspecto] = useState("3/4");
  const cvRef = useRef(null), video = useRef(null), overlay = useRef(null), stream = useRef(null), fileRef = useRef(null);
  const loop = useRef({ prev: null, verdeDesde: null, inicio: Date.now(), ultimo: null, ocupado: false });
  const faseRef = useRef(fase);
  faseRef.current = fase;

  useEffect(() => {
    let vivo = true;
    (async () => {
      try { cvRef.current = (await loadCv()).cv; } catch (e) { if (vivo) { setError(e.message); setFase("error"); } return; }
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
        if (!vivo) { s.getTracks().forEach((t) => t.stop()); return; }
        stream.current = s;
        video.current.srcObject = s;
        await video.current.play().catch(() => {});
        loop.current.inicio = Date.now();
        setFase("camara");
      } catch { if (vivo) { setError("No se pudo abrir la cámara. Revisa el permiso de cámara o usa la cámara normal."); setFase("error"); } }
    })();
    return () => { vivo = false; stream.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const frameCompleto = () => canvasDe(video.current, video.current.videoWidth, video.current.videoHeight);

  const capturar = useCallback((quadPeq, sw) => {
    const cv = cvRef.current, v = video.current;
    const full = frameCompleto();
    const k = v.videoWidth / sw;
    const { canvas, nitidez } = escanear(cv, full, quadPeq.map((p) => ({ x: p.x * k, y: p.y * k })));
    if (nitidez < NITIDEZ_MIN) { setAviso("La foto salió borrosa, tómala de nuevo"); loop.current.verdeDesde = null; return; }
    setAviso(""); setActual({ canvas, manual: false }); setFase("revision");
  }, []);

  useEffect(() => {
    if (fase !== "camara") return undefined;
    const small = document.createElement("canvas");
    const iv = setInterval(() => {
      const v = video.current, L = loop.current, cv = cvRef.current;
      if (!v?.videoWidth || L.ocupado || faseRef.current !== "camara") return;
      L.ocupado = true;
      try {
        const sh = Math.round((SW * v.videoHeight) / v.videoWidth);
        if (small.width !== SW) { small.width = SW; small.height = sh; setAspecto(`${v.videoWidth}/${v.videoHeight}`); }
        small.getContext("2d").drawImage(v, 0, 0, SW, sh);
        const det = detectar(cv, small);
        let msg = evaluar(det);
        if (!msg && movimiento(det.quad, L.prev, SW) > 0.012) msg = "No te muevas";
        L.prev = det.quad;
        if (det.quad) L.ultimo = det.quad;
        const ok = !msg, ahora = Date.now();
        if (ok && !L.verdeDesde) { L.verdeDesde = ahora; navigator.vibrate?.(60); }
        if (!ok) L.verdeDesde = null;
        const o = overlay.current;
        if (o) {
          o.width = SW; o.height = sh;
          const ctx = o.getContext("2d");
          ctx.clearRect(0, 0, SW, sh);
          if (det.quad) {
            ctx.beginPath(); det.quad.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.closePath();
            ctx.lineWidth = 4; ctx.strokeStyle = ok ? "#22c55e" : "#facc15"; ctx.fillStyle = ok ? "rgba(34,197,94,0.18)" : "rgba(250,204,21,0.12)";
            ctx.fill(); ctx.stroke();
          }
        }
        setVerde(ok); setMensaje(msg || "¡Bien! Mantén el celular quieto...");
        setRestante(Math.max(0, Math.ceil((AUTO_MS - (ahora - L.inicio)) / 1000)));
        if (ok && ahora - L.verdeDesde > 1000) capturar(det.quad, SW);
      } catch { /* frame inválido */ } finally { L.ocupado = false; }
    }, 140);
    return () => clearInterval(iv);
  }, [fase, capturar]);

  const disparar = () => { if (verde && loop.current.ultimo) capturar(loop.current.ultimo, SW); };
  const manual = () => {
    const full = frameCompleto();
    const k = video.current.videoWidth / SW;
    setCaptura({ canvas: full, inicial: loop.current.ultimo?.map((p) => ({ x: p.x * k, y: p.y * k })) });
    setFase("ajuste");
  };
  const aplicarAjuste = (pts) => {
    const { canvas, nitidez } = escanear(cvRef.current, captura.canvas, pts);
    setAviso(nitidez < NITIDEZ_MIN ? "Ojo: la imagen se ve borrosa. Si no se lee, repítela." : "");
    setActual({ canvas, manual: true }); setFase("revision");
  };
  const volverCamara = () => {
    setActual(null); setCaptura(null); setAviso("");
    Object.assign(loop.current, { prev: null, verdeDesde: null, inicio: Date.now() });
    setFase(stream.current ? "camara" : "error");
  };
  const desdeArchivo = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 2400 / Math.max(img.width, img.height));
      const canvas = canvasDe(img, Math.round(img.width * k), Math.round(img.height * k));
      let inicial = null;
      if (cvRef.current) {
        try {
          const ks = SW / canvas.width;
          const det = detectar(cvRef.current, canvasDe(canvas, SW, Math.round(canvas.height * ks)));
          if (det.quad) inicial = det.quad.map((p) => ({ x: p.x / ks, y: p.y / ks }));
        } catch { /* sin detección */ }
      }
      setCaptura({ canvas, inicial });
      setFase(cvRef.current ? "ajuste" : "sincv");
    };
    img.src = URL.createObjectURL(f);
  };
  const terminar = async () => {
    setArmando(true);
    try { onDone({ file: await armarPdf(hojas, nombre), manual: hojas.some((h) => h.manual), paginas: hojas.length, portada: hojas[0].canvas.toDataURL("image/jpeg", 0.5) }); }
    finally { setArmando(false); }
  };
  const tiempoAgotado = restante === 0;

  return createPortal(
    <div className="fixed inset-0 z-[300] bg-black text-white flex flex-col" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="escaner-remision">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="font-semibold text-sm">Escanear remisión {hojas.length ? `· ${hojas.length} hoja(s)` : ""}</span>
        <button type="button" onClick={onCancel} className="p-2" data-testid="escaner-cerrar"><X className="h-5 w-5" /></button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={desdeArchivo} data-testid="escaner-archivo-input" />

      <div className={`flex-1 min-h-0 flex-col ${fase === "camara" || fase === "cargando" ? "flex" : "hidden"}`}>
        <div className="mx-3 mb-2 rounded-full px-4 py-2 text-center text-sm font-semibold" style={{ background: verde ? "#16a34a" : "#facc15", color: verde ? "#fff" : "#111" }} data-testid="escaner-mensaje">
          {fase === "cargando" ? "Preparando escáner..." : mensaje}
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center px-2">
          <div className="relative max-h-full" style={{ aspectRatio: aspecto, height: "100%", maxWidth: "100%" }}>
            <video ref={video} playsInline muted autoPlay className="absolute inset-0 w-full h-full object-fill" />
            <canvas ref={overlay} className="absolute inset-0 w-full h-full" data-testid="escaner-overlay" />
            {fase === "cargando" && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>}
          </div>
        </div>
        {aviso && <p className="text-center text-sm text-red-300 pt-2" data-testid="escaner-aviso">{aviso}</p>}
        <div className="px-4 py-3 space-y-2">
          <p className="text-center text-xs text-white/70" data-testid="escaner-auto">{tiempoAgotado ? "Si no se pone verde, usa la captura manual" : `Disparo automático al ponerse verde · ${restante}s`}</p>
          <div className="flex items-center justify-center gap-4">
            {tiempoAgotado && <Button variant="outline" className="h-12 bg-transparent text-white border-white/60" onClick={manual} data-testid="escaner-manual"><Hand className="h-4 w-4 mr-2" />Captura manual</Button>}
            <button type="button" onClick={disparar} disabled={!verde} className={`h-16 w-16 rounded-full border-4 flex items-center justify-center ${verde ? "border-green-500 bg-white" : "border-white/30 bg-white/20"}`} data-testid="escaner-disparar"><Camera className={`h-6 w-6 ${verde ? "text-green-600" : "text-white/50"}`} /></button>
          </div>
        </div>
      </div>

      {fase === "error" && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center" data-testid="escaner-error">
          <p>{error}</p>
          <Button className="h-12 bg-white text-black hover:bg-white/90" onClick={() => fileRef.current?.click()} data-testid="escaner-camara-normal"><Camera className="h-4 w-4 mr-2" />Usar cámara normal</Button>
          <p className="text-xs text-white/60">Después ajustarás las esquinas a mano.</p>
        </div>
      )}
      {fase === "sincv" && captura && (
        <div className="flex-1 flex flex-col p-3 gap-3"><img src={captura.canvas.toDataURL("image/jpeg", 0.8)} alt="Hoja" className="flex-1 min-h-0 object-contain" />
          <Button className="h-12" onClick={() => { setActual({ canvas: captura.canvas, manual: true }); setFase("revision"); }}>Usar esta foto</Button></div>
      )}
      {fase === "ajuste" && captura && <div className="flex-1 min-h-0"><AjusteEsquinas imagen={captura.canvas} inicial={captura.inicial} onAplicar={aplicarAjuste} onCancelar={volverCamara} /></div>}

      {fase === "revision" && actual && (
        <div className="flex-1 min-h-0 flex flex-col p-3 gap-3" data-testid="escaner-revision">
          <img src={actual.canvas.toDataURL("image/jpeg", 0.8)} alt="Hoja escaneada" className="flex-1 min-h-0 object-contain bg-white rounded" data-testid="escaner-preview" />
          {aviso && <p className="text-center text-sm text-amber-300">{aviso}</p>}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-12 bg-transparent text-white border-white/60" onClick={volverCamara} data-testid="escaner-repetir"><RotateCcw className="h-4 w-4 mr-2" />Repetir</Button>
            <Button className="h-12 bg-green-600 hover:bg-green-700" onClick={() => { setHojas((h) => [...h, actual]); setActual(null); setFase("hojas"); }} data-testid="escaner-usar"><Check className="h-4 w-4 mr-2" />Usar esta hoja</Button>
          </div>
        </div>
      )}

      {fase === "hojas" && (
        <div className="flex-1 min-h-0 flex flex-col p-3 gap-3" data-testid="escaner-hojas">
          <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-2 gap-3 content-start">
            {hojas.map((h, i) => (
              <div key={i} className="relative bg-white rounded overflow-hidden" data-testid={`escaner-hoja-${i}`}>
                <img src={h.canvas.toDataURL("image/jpeg", 0.5)} alt={`Hoja ${i + 1}`} className="w-full" />
                <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 px-1.5 rounded">Hoja {i + 1}{h.manual ? " · manual" : ""}</span>
                <button type="button" className="absolute top-1 right-1 bg-black/70 rounded-full p-1" onClick={() => setHojas(hojas.filter((_, j) => j !== i))} data-testid={`escaner-hoja-quitar-${i}`}><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-12 bg-transparent text-white border-white/60" disabled={hojas.length >= 5} onClick={volverCamara} data-testid="escaner-otra-hoja"><Plus className="h-4 w-4 mr-2" />Otra hoja</Button>
            <Button className="h-12 bg-green-600 hover:bg-green-700" disabled={!hojas.length || armando} onClick={terminar} data-testid="escaner-terminar">{armando ? "Creando PDF..." : "Terminar"}</Button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
